// components/export/ExportFacture.js
import React, { useState, useRef, useCallback } from 'react';
import { Button, message, Space, Card, DatePicker, Form, Typography, List, Tag, Alert } from 'antd';
import { CloudUploadOutlined, CheckCircleOutlined, CloseCircleOutlined, SyncOutlined } from '@ant-design/icons';
import axios from 'axios';
import ExportProgress from '../shared/ExportProgress';

const { RangePicker } = DatePicker;
const { Text } = Typography;

// Polling interval in milliseconds
const POLL_INTERVAL = 2500;

const ExportFacture = () => {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [form] = Form.useForm();
  
  // Job tracking state
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [failedFiles, setFailedFiles] = useState([]);
  const [error, setError] = useState(null);
  
  // Ref to store polling interval
  const pollingRef = useRef(null);
  const pollInFlight = useRef(false);
  const mounted = useRef(true);

  const handleDateChange = (dates) => {
    setDateRange(dates);
  };

  // Reset job state
  const resetJobState = () => {
    setJobId(null);
    setJobStatus(null);
    setUploadedFiles([]);
    setFailedFiles([]);
    setError(null);
  };

  // Stop polling
  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  // Poll job status
  const pollJobStatus = useCallback(async (currentJobId) => {
    if (pollInFlight.current || !mounted.current) return;
    pollInFlight.current = true;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/excel/status/${currentJobId}`
      );
      
      if (!mounted.current) return;
      const { status, uploadedFiles: uploaded, failedFiles: failed, error: jobError } = response.data;
      
      setProgress(response.data.progress || { stage: status === 'done' ? 'completed' : ['failed', 'done_with_errors'].includes(status) ? 'failed' : status === 'queued' ? 'queued' : 'starting', percent: status === 'done' ? 100 : 0, message: jobError });
      setJobStatus(status);
      setUploadedFiles(uploaded || []);
      setFailedFiles(failed || []);
      
      if (jobError) {
        setError(jobError);
      }
      
      // Check if job is complete
      if (status === 'done' || status === 'done_with_errors' || status === 'failed') {
        stopPolling();
        setLoading(false);
        
        if (status === 'done') {
          message.success('Export terminé avec succès !');
        } else if (status === 'done_with_errors') {
          message.warning('Export terminé avec des erreurs.');
        } else {
          message.error('Export échoué: ' + (jobError || 'Erreur inconnue'));
        }
      }
    } catch (err) {
      console.error('Error polling job status:', err);
      // Don't stop polling on transient errors, but log them
      if (!mounted.current) return;
      setProgress(previous => ({ ...previous, stage: 'reconnecting', message: err.message }));
      if ([401, 403, 404].includes(err.response?.status)) {
        setProgress(previous => ({ ...previous, stage: 'failed', message: err.response?.data?.message || 'Suivi de l’export indisponible.' }));
        stopPolling();
        setLoading(false);
        const errorMessage = err.response?.data?.message || 'Suivi de l’export indisponible.';
        setError(errorMessage);
        message.error(errorMessage);
      }
    } finally {
      pollInFlight.current = false;
    }
  }, [stopPolling]);

  // Start polling for a job
  const startPolling = useCallback((currentJobId) => {
    stopPolling();
    
    // Set up interval
    pollingRef.current = setInterval(() => {
      pollJobStatus(currentJobId);
    }, POLL_INTERVAL);
    pollJobStatus(currentJobId);
  }, [pollJobStatus, stopPolling]);

  const handleExport = async () => {
    if (loading) return;
    resetJobState();
    setProgress({ stage: 'queued', table: 'Export réponse', percent: 0, totalFiles: 6, uploadedCount: 0 });
    setLoading(true);
    
    try {
      // Prepare the request body with optional date range
      const requestBody = {};
      
      if (dateRange && dateRange[0] && dateRange[1]) {
        requestBody.startDate = dateRange[0].format('YYYY-MM-DD');
        requestBody.endDate = dateRange[1].format('YYYY-MM-DD');
      }
      
      // Start the export job
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/excel/export`, 
        requestBody
      );
      
      if (!mounted.current) return;
      const { jobId: newJobId, status } = response.data;
      
      if (newJobId) {
        setJobId(newJobId);
        setJobStatus(status);
        if (response.data.progress) setProgress(response.data.progress);
        message.info('Export démarré. Veuillez patienter...');
        
        // Start polling for status updates
        startPolling(newJobId);
      } else {
        throw new Error('No job ID returned from server');
      }
    } catch (err) {
      if (!mounted.current) return;
      console.error('Export error:', err);
      
      // Handle case where export is already running (409 Conflict)
      if (err.response?.status === 409 && err.response?.data?.existingJobId) {
        const existingJobId = err.response.data.existingJobId;
        message.warning('Un export est déjà en cours. Suivi de l\'export existant...');
        setJobId(existingJobId);
        setJobStatus(err.response.data.existingJobStatus);
        startPolling(existingJobId);
        return; // Keep loading state true while polling
      }
      
      setLoading(false);
      setError(err.response?.data?.message || err.message);
      setProgress(previous => ({ ...previous, stage: 'failed', message: err.response?.data?.message || err.message }));
      message.error('Erreur lors du démarrage de l\'export: ' + (err.response?.data?.message || err.message));
    }
  };

  // Get status display info
  const getStatusTag = () => {
    switch (jobStatus) {
      case 'queued':
        return <Tag icon={<SyncOutlined spin />} color="default">En attente</Tag>;
      case 'running':
        return <Tag icon={<SyncOutlined spin />} color="processing">En cours</Tag>;
      case 'done':
        return <Tag icon={<CheckCircleOutlined />} color="success">Terminé</Tag>;
      case 'done_with_errors':
        return <Tag icon={<CloseCircleOutlined />} color="warning">Terminé avec erreurs</Tag>;
      case 'failed':
        return <Tag icon={<CloseCircleOutlined />} color="error">Échoué</Tag>;
      default:
        return null;
    }
  };

  // Cleanup on unmount
  React.useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      stopPolling();
    };
  }, [stopPolling]);

  return (
    <Card title="Export" style={{ maxWidth: 600, margin: '20px auto' }}>
      <ExportProgress progress={progress} title="Export réponse" />
      <Form form={form} layout="vertical">
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Text>
            Exporter les Factures, Bonlivs et Avoirs validés. Par défaut, les 5 derniers jours seront exportés.
          </Text>
          
          <Form.Item label="Période d'export (optionnel)" style={{ marginBottom: 0 }}>
            <RangePicker 
              style={{ width: '100%' }}
              onChange={handleDateChange}
              format="DD-MM-YYYY"
              disabled={loading}
            />
          </Form.Item>
          
          <Text type="secondary">
            {dateRange && dateRange[0] && dateRange[1] 
              ? `Période sélectionnée: ${dateRange[0].format('DD-MM-YYYY')} - ${dateRange[1].format('DD-MM-YYYY')}` 
              : "Aucune période sélectionnée. Les 5 derniers jours seront exportés."}
          </Text>
          
          <Button
            type="primary"
            icon={<CloudUploadOutlined />}
            loading={loading}
            onClick={handleExport}
            size="large"
            block
            disabled={loading}
          >
            {loading ? 'Exportation en Cours...' : "Démarrez l'export"}
          </Button>
          
          {/* Job Status Display */}
          {jobStatus && (
            <Card size="small" style={{ marginTop: 16 }}>
              <Space direction="vertical" style={{ width: '100%' }} size="small">
                <Space>
                  <Text strong>Statut:</Text>
                  {getStatusTag()}
                </Space>
                
                {uploadedFiles.length > 0 && (
                  <div>
                    <Text strong style={{ color: '#52c41a' }}>Fichiers uploadés:</Text>
                    <List
                      size="small"
                      dataSource={uploadedFiles}
                      renderItem={(file) => (
                        <List.Item style={{ padding: '4px 0' }}>
                          <Space>
                            <CheckCircleOutlined style={{ color: '#52c41a' }} />
                            <Text>{file}</Text>
                          </Space>
                        </List.Item>
                      )}
                    />
                  </div>
                )}
                
                {failedFiles.length > 0 && (
                  <div>
                    <Text strong style={{ color: '#ff4d4f' }}>Fichiers échoués:</Text>
                    <List
                      size="small"
                      dataSource={failedFiles}
                      renderItem={(item) => (
                        <List.Item style={{ padding: '4px 0' }}>
                          <Space direction="vertical" size={0}>
                            <Space>
                              <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                              <Text>{item.file}</Text>
                            </Space>
                            <Text type="secondary" style={{ fontSize: 12, marginLeft: 22 }}>
                              {item.error}
                            </Text>
                          </Space>
                        </List.Item>
                      )}
                    />
                  </div>
                )}
                
                {error && jobStatus === 'failed' && (
                  <Alert 
                    type="error" 
                    message="Erreur" 
                    description={error}
                    showIcon
                  />
                )}
              </Space>
            </Card>
          )}
        </Space>
      </Form>
    </Card>
  );
};

export default ExportFacture;
